import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useLocation } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { StatInformation } from "../../domain/models/stat_information";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { PatrimonioMaquinaEquipListaModel } from "../../domain/models/patrimonio_maquina_equip_lista";
import { PatrimonioMaquinaEquipModel } from "../../domain/models/patrimonio_maquina_equip";
import { usePatrimonioMaquinaEquipListaPagination } from "../hooks/PatrimonioInspecao_cadastro/pagination";
import { PatrimonioMaquinaEquipForm } from "../schemas/PatrimonioInspecao_cadastro/patrimonio_maquina_equip";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { usePatrimonioMaquinaEquipListaFilter } from "../hooks/PatrimonioInspecao_cadastro/filters";
import { PatrimonioChecklistInfo } from "../../domain/models/patrimonio_checklist_info";

/**
 * LoadPatrimonioInspecaoFuncState
 */

type LoadPatrimonioMaquinaEquipFuncState = {
  idPatrimonioCentroCusto?: number;
  status?: number;
  descricao?: string;

  page?: number;
  rowsPerPage?: number;
};


export const usePatrimonioCadastroInspecaoViewModel = () => {

  const location = useLocation();
  const backOrNavigate = useBackOrNavigate();
  const debouncedRef = useCreateDebounceHandler(750);

  const {
    setPageSaving,
    pushInfoNotification,
    pushWarnNotification,
    pushErrorNotification,
  } = usePageLoader();

  const {
    commons: {
      getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase,
    },
    controlePatrimonio: {
      getAllPatrimonioMaquinaEquipUseCase,
      getPatrimonioMaquinaEquipByIdUseCase,
      createPatrimonioMaquinaEquipUseCase,
      updatePatrimonioMaquinaEquipUseCase,
      deletePatrimonioMaquinaEquipUseCase,
      getChecklistMaquinaEquipDropDownUseCase,
      getPatrimonioChecklistInfoUseCase,
    }
  } = useDependencyInjection();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idPatrimonioCentroCusto, setIdPatrimonioCentroCusto] = useState<number>();

  // const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();
  const [dropDownChecklistMaquinaEquip, setDropDownChecklistMaquinaEquip] = useState<DropDown[]>();
  const [patrimonioMaquinaEquips, setPatrimonioMaquinaEquips] = useState<PatrimonioMaquinaEquipListaModel[]>();
  const [patrimonioChecklistInfo, setPatrimonioChecklistInfo] = useState<PatrimonioChecklistInfo>();

  const [selectedPatrimonioMaquinaEquip, setSelectedPatrimonioMaquinaEquip] = useState<PatrimonioMaquinaEquipModel>();
  const [selectedIdDeletePatrimonioMaquinaEquip, setSelectedIdDeletePatrimonioMaquinaEquip] = useState<number | undefined>();

  const [openPatrimonioMaquinaEquipModal, setOpenPatrimonioMaquinaEquipModal] = useState(false);
  const [openPatrimonioMaquinaEquipDelete, setOpenPatrimonioMaquinaEquipDelete] = useState(false);
  const [openIncluirChecklistMaquinaEquip, setOpenIncluirChecklistMaquinaEquip] = useState(false);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

  const filters = usePatrimonioMaquinaEquipListaFilter();
  const pagination = usePatrimonioMaquinaEquipListaPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: e => pushErrorNotification("Erro", [e]),
  });

  /**
   * Funções de carregamento
   */

  const loadPatrimonioChecklistInfo = useCallback(async (idPatrimonioCentroCusto: number, idPatrimonioMaquinaEquip?: number, idInspecaoPatrimonio?: number) => {
    try {
      const response = await getPatrimonioChecklistInfoUseCase.getPatrimonioChecklistInfo(
        idPatrimonioCentroCusto,
        idPatrimonioMaquinaEquip,
        idInspecaoPatrimonio,
      );
      setPatrimonioChecklistInfo(response);
    } catch (e: unknown) {
      throw new Error("Falha ao carregar informações do checklist do patrimônio!");
    }
  }, [
    getPatrimonioChecklistInfoUseCase,
  ]);

  const loadChecklistMaquinaEquipDropDown = useCallback(async () => {
    try {
      const patrimonioMaquinaEquips = await getChecklistMaquinaEquipDropDownUseCase
        .getChecklistMaquinaEquipDropDown();
      setDropDownChecklistMaquinaEquip(patrimonioMaquinaEquips);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados da pagina!'));
    }
  }, [
    pushErrorNotification,
    getChecklistMaquinaEquipDropDownUseCase,
  ]);



  const loadAllPatrimonioMaquinaEquip = useCallback(async (fs?: LoadPatrimonioMaquinaEquipFuncState) => {
    const filteredIdPatrimonioCentroCusto = fs?.idPatrimonioCentroCusto ?? idPatrimonioCentroCusto;
    const filteredStatus = evaluateParamValue(fs?.status, filters.status);
    const filteredDescricao = evaluateParamValue(fs?.descricao, filters.descricao);

    const filteredPage = fs?.page ?? pagination.page;
    const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

    await getAllPatrimonioMaquinaEquipUseCase.getAllPatrimonioMaquinaEquip(
      filteredIdPatrimonioCentroCusto ?? 0,
      filteredDescricao ?? undefined,
      filteredStatus ?? undefined,
      filteredPage ?? undefined,
      filteredRowsPerPage ?? undefined,
    );

    setPatrimonioMaquinaEquips(getAllPatrimonioMaquinaEquipUseCase.items);
    pagination.setTotalItems(getAllPatrimonioMaquinaEquipUseCase.totalItems);
  }, [
    idPatrimonioCentroCusto,
    filters,
    pagination,
    getAllPatrimonioMaquinaEquipUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get('id') ?? null;
      const locationIdPatrimonioCentroCusto = locationId ? Number(locationId) : undefined;
      const jobs = [];

      setIdPatrimonioCentroCusto(locationIdPatrimonioCentroCusto);

      if (locationIdPatrimonioCentroCusto) {
        jobs.push(loadChecklistMaquinaEquipDropDown());
        jobs.push(loadPatrimonioChecklistInfo(locationIdPatrimonioCentroCusto));
        jobs.push(loadAllPatrimonioMaquinaEquip({ idPatrimonioCentroCusto: locationIdPatrimonioCentroCusto, page: 1 }));
      }
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    if (idPatrimonioCentroCusto) {
      const jobs = [];
      jobs.push(loadAllPatrimonioMaquinaEquip({ idPatrimonioCentroCusto: idPatrimonioCentroCusto, page: 1 }));
      await Promise.all(jobs);
    }
  }, [
    idPatrimonioCentroCusto,
    loadAllPatrimonioMaquinaEquip,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/Patrimonio_maquinaEquip' });
  }, [
    backOrNavigate,
  ]);

  const handleOpenChecklist = useCallback((idPatrimonioMaquinaEquip: number, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/PatrimonioItensInspecao_cadastro?id=${idPatrimonioCentroCusto}&idPatrimonioMaquinaEquip=${idPatrimonioMaquinaEquip}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/PatrimonioItensInspecao_cadastro?id=${idPatrimonioCentroCusto}&idPatrimonioMaquinaEquip=${idPatrimonioMaquinaEquip}`, { alternativeRoute: `/PatrimonioItensInspecao_cadastro?id=${idPatrimonioCentroCusto}` });
    }
  }, [
    idPatrimonioCentroCusto,
    backOrNavigate,
  ]);

  const handleSubmitPatrimonioMaquinaEquip = useCallback(async (form: PatrimonioMaquinaEquipForm) => {
    if (idPatrimonioCentroCusto) {
      try {
        setSaving(true);
        setPageSaving(true);
        if (selectedPatrimonioMaquinaEquip) {
          await updatePatrimonioMaquinaEquipUseCase.updatePatrimonioMaquinaEquip(
            selectedPatrimonioMaquinaEquip.idPatrimonioMaquinaEquip,
            idPatrimonioCentroCusto,
            form.idChecklistMaquinaEquip,
            form.descricao,
            form.status
          );
        } else {
          await createPatrimonioMaquinaEquipUseCase.createPatrimonioMaquinaEquip(
            idPatrimonioCentroCusto,
            form.idChecklistMaquinaEquip,
            form.descricao,
            form.status,
          );
        }

        await loadAllPatrimonioMaquinaEquip();

        setOpenPatrimonioMaquinaEquipModal(false);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idPatrimonioCentroCusto,
    selectedPatrimonioMaquinaEquip,
    createPatrimonioMaquinaEquipUseCase,
    updatePatrimonioMaquinaEquipUseCase,
    loadAllPatrimonioMaquinaEquip,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleOpenPatrimonioMaquinaEquipModal = useCallback(async (
    idPatrimonioMaquinaEquip: number | null,
    openState: boolean,
  ) => {
    try {
      if (idPatrimonioCentroCusto) {
        if (openState) {
          setLoading(true);
          if (idPatrimonioMaquinaEquip && patrimonioMaquinaEquips) {
            const patrimonioMaquinaEquip = patrimonioMaquinaEquips.find(x => x.idPatrimonioMaquinaEquip === idPatrimonioMaquinaEquip);
            setSelectedPatrimonioMaquinaEquip(patrimonioMaquinaEquip);
            // await loadChecklistMaquinaEquipDropDown();
          } else {
            setSelectedPatrimonioMaquinaEquip(undefined);
          }
        }
      }
      setOpenPatrimonioMaquinaEquipModal(openState);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    idPatrimonioCentroCusto,
    patrimonioMaquinaEquips,
    // loadChecklistMaquinaEquipDropDown,
    pushErrorNotification,
  ]);


  const handleDeletePatrimonioMaquinaEquipOpenStateModal = useCallback((idPatrimonioMaquinaEquip: number | null, openState: boolean) => {
    if (openState) {
      if (idPatrimonioMaquinaEquip) {
        setSelectedIdDeletePatrimonioMaquinaEquip(idPatrimonioMaquinaEquip);
      } else {
        setSelectedIdDeletePatrimonioMaquinaEquip(undefined);
      }
    }
    setOpenPatrimonioMaquinaEquipDelete(openState);
  }, [
  ]);

  const handleDeletePatrimonioMaquinaEquipConfirm = useCallback(async () => {
    try {
      if (selectedIdDeletePatrimonioMaquinaEquip) {
        setSaving(true);

        await deletePatrimonioMaquinaEquipUseCase.deletePatrimonioMaquinaEquip(
          selectedIdDeletePatrimonioMaquinaEquip);

        await loadAllPatrimonioMaquinaEquip();

        setOpenPatrimonioMaquinaEquipDelete(false);
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao excluir registro!"));
    } finally {
      setSaving(false);
      setSelectedIdDeletePatrimonioMaquinaEquip(undefined)
    }
  }, [
    selectedIdDeletePatrimonioMaquinaEquip,
    deletePatrimonioMaquinaEquipUseCase,
    loadAllPatrimonioMaquinaEquip,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleOpenIncluirChecklistMaquinaEquipModal = useCallback(async (openState: boolean) => {
    if (idPatrimonioCentroCusto) {
      if (openState) {
        try {
          setLoading(true);
          await loadChecklistMaquinaEquipDropDown();
          setOpenIncluirChecklistMaquinaEquip(openState);
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        } finally {
          setLoading(false);
        }
      } else {
        setOpenIncluirChecklistMaquinaEquip(openState);
      }
    }
  }, [
    idPatrimonioCentroCusto,
    loadChecklistMaquinaEquipDropDown,
    pushErrorNotification,
  ]);


  const handleOpenExcluirPatrimonioMaquinaEquipModal = useCallback(async (openState: boolean) => {
    if (idPatrimonioCentroCusto) {
      if (openState) {
        try {
          setLoading(true);

        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        } finally {
          setLoading(false);
        }
      } else {
      }
    }
  }, [
    idPatrimonioCentroCusto,
    pushErrorNotification,
  ]);

  const handleDeleteLotePatrimonioMaquinaEquipConfirm = useCallback(async () => {
    try {
      if (idPatrimonioCentroCusto) {
        setSaving(true);
        setPageSaving(true);
        if (!rowSelectionModel || rowSelectionModel.ids.size === 0) {
          pushWarnNotification("Atenção", "Você deve selecionar as área a serem excluidas!");
          return;
        }
        for (const id of rowSelectionModel.ids) {
          const patrimonioMaquinaEquip = patrimonioMaquinaEquips?.find(x => x.idPatrimonioMaquinaEquip === Number(id));
          if (patrimonioMaquinaEquip) {
            await deletePatrimonioMaquinaEquipUseCase.deletePatrimonioMaquinaEquip(patrimonioMaquinaEquip.idPatrimonioMaquinaEquip);
          }
        }
        await loadAllPatrimonioMaquinaEquip();
        setSelectedPatrimonioMaquinaEquip(undefined);
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    idPatrimonioCentroCusto,
    rowSelectionModel,
    patrimonioMaquinaEquips,
    deletePatrimonioMaquinaEquipUseCase,
    loadAllPatrimonioMaquinaEquip,
    pushErrorNotification,
    pushInfoNotification,
    pushWarnNotification,
    setPageSaving,
  ]);

  //#region Filters

  const handleStatusChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    filters.setStatus(newValue);
    return async () => {
      try {
        setLoading(true);
        pagination.setPage(1);
        await loadAllPatrimonioMaquinaEquip({ page: 1, status: newValue });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar checklist de serviços"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pagination,
    loadAllPatrimonioMaquinaEquip,
    pushErrorNotification,
  ]);

  const handleDescricaoChange = useDebouncedCallback(debouncedRef, (newValue?: string) => {
    filters.setDescricao(newValue);
    return async () => {
      try {
        setLoading(true);
        pagination.setPage(1);
        await loadAllPatrimonioMaquinaEquip({ page: 1, descricao: newValue });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar checklist de serviços"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pagination,
    loadAllPatrimonioMaquinaEquip,
    pushErrorNotification,
  ]);

  const handleClearFilter = useCallback(async () => {
    try {
      setLoading(true);
      pagination.setPage(1);
      filters.setStatus(2);
      filters.setDescricao("");
      await loadAllPatrimonioMaquinaEquip({ status: 2, descricao: "", page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    filters,
    loadAllPatrimonioMaquinaEquip,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      await loadAllPatrimonioMaquinaEquip({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadAllPatrimonioMaquinaEquip,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      await loadAllPatrimonioMaquinaEquip({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadAllPatrimonioMaquinaEquip,
    pushErrorNotification,
  ]);

  const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
    setRowSelectionModel(rows);
  }, [
  ]);

  //#endregion Filters


  // const handleOpenChangeHistory = useCallback((
  //   idTabela: number | null,
  //   tipoTabela: LogTipoTabela | null,
  // ) => {
  //   if (idTabela && tipoTabela) {
  //     changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
  //   } else {
  //     if (idObra) {
  //       changeHistory.handleOpenChangeHistory(idObra, LogTipoTabela.OBRA, true);
  //     }
  //   }
  // }, [
  //   idObra,
  //   changeHistory,
  // ]);

  return {
    loading,
    saving,
    errorMessage,
    idPatrimonioCentroCusto,
    patrimonioMaquinaEquips: patrimonioMaquinaEquips ?? [],
    selectedPatrimonioMaquinaEquip,
    openPatrimonioMaquinaEquipModal,
    openIncluirChecklistMaquinaEquip,
    dropDownChecklistMaquinaEquip,
    rowSelectionModel,
    openPatrimonioMaquinaEquipDelete,
    filters,
    pagination,
    changeHistory,
    patrimonioChecklistInfo,

    handleReload,
    handleBack,
    handleSubmitPatrimonioMaquinaEquip,
    handleOpenChecklist,
    handleOpenPatrimonioMaquinaEquipModal,
    handleDeletePatrimonioMaquinaEquipOpenStateModal,
    handleDeletePatrimonioMaquinaEquipConfirm,
    handleStatusChange,
    handleDescricaoChange,
    handleClearFilter,
    handlePageChange,
    handleRowsPerPageChange,
    handleRowSelectionModelChange,

  };

}